<script>
import { mapActions } from 'vuex';
import { cloneDeep, result } from 'lodash';
import RbxRemoteCombobox from '@/shared/reybex/RbxRemoteCombobox';
import RbxRemoteComboboxMultiple from '@/shared/reybex/RbxRemoteComboboxMultiple';
import validatorMixin from '@/mixins/validator';
import snackbarMixin from '@/mixins/snackbar';
import paginationMixin from '@/mixins/pagination';

export default {
  name: 'NewProcedureDirectoriesDialog',
  components: {
    RbxRemoteCombobox,
    RbxRemoteComboboxMultiple,
  },
  mixins: [snackbarMixin, validatorMixin, paginationMixin],
  data() {
    return {
      visible: false,
      isLoading: false,
      rules: {
        name: { required: true },
      },
      baseFormData: {
      },
      formData: {},
      templates: [],
      templateList: { data: [], total: 0 },
      selected: [],
      headers: [{ text: 'Bezeichnung', value: 'name' }],
      initialExamState: { id: 1, translatedName: 'offen' },
    };
  },
  created() {
    this.formData = cloneDeep(this.baseFormData);
  },
  mounted() {
    this.visible = true;
    this.fetchTemplates()
      .then((response) => {
        this.templateList.data = response.data.map(it => ({ id: it.id, name: it.name }));
        this.templateList.total = response.meta;
      });
  },
  beforeDestroy() {
    this.visible = false;
  },
  methods: {
    ...mapActions('procedureDirectoryDetails', ['saveProcedureDirectory', 'fetchTemplates']),
    ...mapActions('procedureDirectoryList', { fetchData: 'fetchProcedureDirectoryList' }),
    submit(type) {
      if (!this.validateForm()) return false;

      this.isLoading = true;

      this.formData.templates = this.selected;

      return this.saveProcedureDirectory(this.formData)
        .then(({ data }) => {
          this.showSuccessMessage('Das Audit wurde angelegt.');

          if (type === 'new') {
            this.formData = cloneDeep(this.baseFormData);
          } else {
            this.close();

            if (type === 'detail') {
              this.$router.push(`/procedureDirectory/${data.id}`);
            }
          }
          this.fetchData();
        })
        .catch(({ response }) => {
          const message = result(response, 'data.data.error', 'Es ist ein Fehler aufgetreten.');
          this.showErrorMessage(message);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    close() {
      this.$emit('cancel');
    },
    onPrivacyOfficerResponsible() {
      if (this.formData.isPrivacyOfficerResponsible) {
        this.formData.privacyOfficer = null;
      }
    },
    onClientRespAffiliate() {
      if (this.formData.isClientRespAffiliate) {
        this.formData.responsibleAffiliate = null;
      }
    },
    setSelected(template) {
      let index = 0;
      let isTemplateFound = false;

      while (index < this.selected.length) {
        if (this.selected[index].id === template.id) {
          isTemplateFound = true;
          break;
        } else {
          index += 1;
        }
      }

      if (isTemplateFound) {
        this.selected.splice(index, 1);
      } else {
        console.log('push selected', template);
        this.selected.push(template);
      }
    },
  },
};
</script>

<template>
  <v-dialog v-model="visible"
            persistent
            max-width="1000px">

    <v-card>
      <v-card-title class="title pa-3">
        Neue Verfahren
      </v-card-title>

      <v-card-text>
        <v-container grid-list-md class="pa-0">
          <v-layout row wrap>

            <v-flex xs12 md6 py-0>
              <v-text-field
                v-model="formData.name"
                :error-messages="validationErrorMessages.name"
                label="Bezeichnung"
                height="22"
                class="caption"
              />
            </v-flex>

            <v-flex xs12 md6 py-0>
              <rbx-remote-combobox
                v-model="formData.examState"
                :selected="initialExamState"
                label="Status"
                domain="ExamState"
              />
            </v-flex>

            <v-flex xs12 md6 py-0>
              <rbx-remote-combobox
                v-model="formData.responsibleUser"
                label="Bearbeiter"
                domain="User"
              />
            </v-flex>

            <v-flex xs12 md6 py-0>
              <rbx-remote-combobox
                v-model="formData.organizationalUnit"
                label="Organisationseinheit"
                domain="OrganizationalUnit"
              />
            </v-flex>

            <v-flex xs12 md6 py-0>
              <rbx-remote-combobox
                v-model="formData.responsibleAffiliate"
                label="Verantwortlicher"
                domain="Affiliate"
                :disabled="formData.isClientRespAffiliate"
              />
            </v-flex>

            <v-flex xs12 md6 py-0>
              <v-checkbox
                v-model="formData.isClientRespAffiliate"
                label="Mandant selbst verantwortlich"
                class="caption"
                hide-details
                @click.native="onClientRespAffiliate"
              />
            </v-flex>

            <v-flex xs12 md6 py-0>
              <v-text-field
                v-model="formData.privacyOfficer"
                label="Datenschutzbeauftragter"
                height="22"
                class="caption"
                :disabled="formData.isPrivacyOfficerResponsible"
              />
            </v-flex>

            <v-flex xs12 md6 py-0>
              <v-checkbox
                v-model="formData.isPrivacyOfficerResponsible"
                label="Unternehmens-Datenschutzbeauftragter ist zuständig"
                class="caption"
                hide-details
                @click.native="onPrivacyOfficerResponsible"
              />
            </v-flex>

          </v-layout>

          <v-data-table
            v-model="selected"
            select-all
            :headers="headers"
            :items="templateList.data"
            :disable-initial-sort="true"
            :pagination="pagination"
            :loading="templateList.isLoading"
            :rows-per-page-items="rowsPerPageItems"
            :total-items="this.pagination.totalItems"
            :expand="true"
            item-key="id"
            @update:pagination="loadNextPage"
            rows-per-page-text="Zeilen pro Seite"
            no-data-text="keine Daten verfügbar"
            class="elevation-1 js-table"
          >
            <template slot="items" slot-scope="props">
              <tr>
                <td>
                  <v-checkbox
                    :input-value="props.selected"
                    primary
                    hide-details
                    @change="setSelected({id: props.item.id, name: props.item.name})"
                  />
                </td>
                <td>{{ props.item.name }}</td>
              </tr>
            </template>
            <template slot="pageText" slot-scope="{ pageStart, pageStop }">
              von {{ pageStart }} bis {{ pageStop }}
            </template>
          </v-data-table>


        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer/>
        <v-btn @click.native="close"
               color="red"
               flat>
          Schließen
        </v-btn>

        <v-btn @click.native="submit"
               color="blue"
               :loading="isLoading"
               flat>
          Speichern
        </v-btn>
      </v-card-actions>
    </v-card>

  </v-dialog>
</template>
